/**
 * De bewaking van het dashboard per sturingslaag (K10, bouwspec §6).
 *
 * * `/dashboard` stuurt door naar de laatst gebruikte toegestane laag, of de
 *   eerste in de volgorde strategisch, tactisch, operationeel. Oude links met
 *   `?tab=` komen op de bijbehorende laag uit.
 * * Een laag die niet bij de sturingsrollen hoort, stuurt door naar een die
 *   wel mag, met een melding; geen lege pagina.
 * * Geen sturingsrol: een uitleg dat de beheerder er een moet toekennen.
 *
 * De backend weigert hetzelfde met 403 (`require_layer`); dit is wat het
 * scherm aanbiedt, niet de beveiliging.
 */
import { Compass } from "lucide-react";
import type { ReactNode } from "react";
import { Navigate, useLocation, useParams } from "react-router";

import { PageHeader } from "@/components/layout/PageHeader";
import { EmptyState } from "@/components/state/EmptyState";
import { Skeleton } from "@/components/ui/Skeleton";

import {
  type SteeringLayer,
  lastUsedLayer,
  layerById,
  useSteeringLayers,
} from "./steeringLayers";

/** De tabbladnamen van vóór de lagen routes werden, zodat bewaarde links blijven werken. */
const OLD_TABS: Record<string, SteeringLayer["id"]> = {
  strategisch: "strategisch",
  tactisch: "tactisch",
  operationeel: "operationeel",
  overzicht: "strategisch",
  financieel: "strategisch",
  beleid: "strategisch",
  onderhoud: "tactisch",
};

/** Melding die mee gaat als iemand op een laag kwam die niet bij zijn rollen hoort. */
export type LayerRedirectState = { refusedLayer?: string };

export function DashboardIndex() {
  const { layers, known } = useSteeringLayers();
  const { search, hash } = useLocation();
  if (!known) return <LayerLoading />;
  if (layers.length === 0) return <NoDashboard />;
  const gevraagd = OLD_TABS[new URLSearchParams(search).get("tab") ?? ""];
  const voorkeur = [gevraagd, lastUsedLayer()].find((id) => id && layers.some((l) => l.id === id));
  return <Navigate to={`/dashboard/${voorkeur ?? layers[0].id}${hash}`} replace />;
}

export function RequireLayer({ children }: { children: (layer: SteeringLayer, allowed: SteeringLayer[]) => ReactNode }) {
  const { laag } = useParams();
  const { layers, known } = useSteeringLayers();
  if (!known) return <LayerLoading />;
  if (layers.length === 0) return <NoDashboard />;
  const gevraagd = layerById(laag);
  const toegestaan = gevraagd && layers.find((l) => l.id === gevraagd.id);
  if (!toegestaan) {
    const state: LayerRedirectState = { refusedLayer: gevraagd?.label ?? laag };
    return <Navigate to={`/dashboard/${layers[0].id}`} replace state={state} />;
  }
  return <>{children(toegestaan, layers)}</>;
}

function LayerLoading() {
  return (
    <div className="atl-dashboard-page" aria-busy="true">
      <Skeleton height="12rem" />
    </div>
  );
}

export function NoDashboard() {
  return (
    <div className="atl-dashboard-page">
      <PageHeader title="Dashboard" hideExport />
      <EmptyState
        icon={<Compass size={22} strokeWidth={1.5} />}
        title="Nog geen dashboard toegewezen"
        description="Welk dashboard je ziet, hangt af van je sturingsrol: strategisch, tactisch of operationeel. Vraag de beheerder om er een toe te kennen onder Gebruikers. De andere schermen blijven bereikbaar zoals je rechten het toestaan."
      />
    </div>
  );
}
